import { useState } from 'react';
import { useSessionDevices } from '../../../hooks/useSessionDevices';
import { parseUserAgent } from '../../../lib/sessionDevice';

const fmtDate = (d: string) => {
  const date = new Date(d);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 2) return 'Agora mesmo';
  if (diffMin < 60) return `Há ${diffMin} minuto${diffMin > 1 ? 's' : ''}`;
  if (diffH < 24) return `Há ${diffH} hora${diffH > 1 ? 's' : ''}`;
  if (diffD < 7) return `Há ${diffD} dia${diffD > 1 ? 's' : ''}`;
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtCreated = (d: string) =>
  new Date(d).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const DeviceTypeIcon = ({ type }: { type: string }) => {
  const icon =
    type === 'mobile'
      ? 'ri-smartphone-line'
      : type === 'tablet'
      ? 'ri-tablet-line'
      : 'ri-computer-line';
  return (
    <div className="w-10 h-10 flex items-center justify-center">
      <i className={`${icon} text-xl text-gray-500`} />
    </div>
  );
};

const OsIcon = ({ os }: { os: string }) => {
  const { osIcon } = parseUserAgent(`(${os})`);
  const icon =
    os === 'iOS' || os === 'iPadOS' || os === 'macOS'
      ? 'ri-apple-fill'
      : os === 'Android'
      ? 'ri-android-fill'
      : os === 'Windows'
      ? 'ri-windows-fill'
      : os === 'Linux'
      ? 'ri-ubuntu-fill'
      : osIcon;
  return <i className={`${icon} text-sm text-gray-400`} />;
};

export function SessionDevicesTab() {
  const { sessions, loading, revoking, fetchSessions, revokeSession, revokeAllOthers } =
    useSessionDevices();
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const otherSessions = sessions.filter(s => !s.isCurrent);

  const handleRevoke = async (id: string) => {
    await revokeSession(id);
    setConfirmRevokeId(null);
  };

  const handleRevokeAll = async () => {
    await revokeAllOthers();
    setConfirmRevokeAll(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-sm font-700 text-gray-900">Dispositivos conectados</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Gerencie as sessões ativas da sua conta em todos os dispositivos.
            </p>
          </div>
          <button
            onClick={fetchSessions}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Session list */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-300" />
            <p className="text-sm text-gray-400">Carregando dispositivos...</p>
          </div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 flex flex-col items-center justify-center gap-3">
          <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center">
            <i className="ri-computer-line text-gray-300 text-2xl" />
          </div>
          <p className="text-sm text-gray-400">Nenhuma sessão ativa encontrada.</p>
          <button
            onClick={fetchSessions}
            className="text-xs font-600 text-emerald-600 hover:text-emerald-700 cursor-pointer"
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(session => (
            <div
              key={session.id}
              className={`bg-white rounded-2xl border p-4 sm:p-5 transition-all ${
                session.isCurrent ? 'border-emerald-200' : 'border-gray-100'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Device icon */}
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                    session.isCurrent ? 'bg-emerald-50' : 'bg-gray-50'
                  }`}
                >
                  <DeviceTypeIcon type={session.device_type} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-700 text-gray-900">
                      {session.session_label ?? `${session.os_name} · ${session.browser_name}`}
                    </span>
                    {session.isCurrent && (
                      <span className="inline-flex items-center gap-1 text-xs font-600 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                        Este dispositivo
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                    {/* OS */}
                    <span className="flex items-center gap-1">
                      <OsIcon os={session.os_name} />
                      {session.os_name}
                    </span>

                    {/* Browser */}
                    <span className="flex items-center gap-1">
                      <i className="ri-global-line text-xs" />
                      {session.browser_name}
                    </span>

                    {/* IP */}
                    {session.ip_address && (
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-xs" />
                        {session.ip_address}
                      </span>
                    )}

                    {/* Device type */}
                    <span className="flex items-center gap-1">
                      <i
                        className={`text-xs ${
                          session.device_type === 'mobile'
                            ? 'ri-smartphone-line'
                            : session.device_type === 'tablet'
                            ? 'ri-tablet-line'
                            : 'ri-computer-line'
                        }`}
                      />
                      {session.device_type === 'mobile'
                        ? 'Celular'
                        : session.device_type === 'tablet'
                        ? 'Tablet'
                        : 'Computador'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 mt-1.5">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line text-xs" />
                      Último acesso: <strong className="text-gray-600 font-500">{fmtDate(session.last_active_at)}</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-login-box-line text-xs" />
                      Conectado em: <strong className="text-gray-600 font-500">{fmtCreated(session.created_at)}</strong>
                    </span>
                  </div>
                </div>

                {/* Revoke button */}
                {!session.isCurrent && (
                  <div className="shrink-0">
                    {confirmRevokeId === session.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 whitespace-nowrap">Confirmar?</span>
                        <button
                          onClick={() => handleRevoke(session.id)}
                          disabled={revoking === session.id}
                          className="flex items-center gap-1 text-xs font-600 px-2.5 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                        >
                          {revoking === session.id && <i className="ri-loader-4-line animate-spin" />}
                          Sim, desconectar
                        </button>
                        <button
                          onClick={() => setConfirmRevokeId(null)}
                          className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRevokeId(session.id)}
                        className="flex items-center gap-1.5 text-xs font-600 px-3 py-1.5 text-red-500 hover:bg-red-50 border border-red-100 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                      >
                        <i className="ri-logout-box-line text-sm" />
                        Desconectar
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Revoke all others */}
      {otherSessions.length > 1 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center shrink-0">
              <i className="ri-logout-box-r-line text-red-500 text-base" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-700 text-gray-900 mb-0.5">Sair de todos os outros dispositivos</p>
              <p className="text-xs text-gray-400 mb-3">
                Desconecta {otherSessions.length} outros dispositivos de uma só vez. Esta ação não afeta o dispositivo atual.
              </p>
              {confirmRevokeAll ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-600">Tem certeza?</span>
                  <button
                    onClick={handleRevokeAll}
                    disabled={revoking === 'all'}
                    className="flex items-center gap-1.5 text-xs font-600 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                  >
                    {revoking === 'all' && <i className="ri-loader-4-line animate-spin" />}
                    Sim, desconectar todos
                  </button>
                  <button
                    onClick={() => setConfirmRevokeAll(false)}
                    className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmRevokeAll(true)}
                  className="flex items-center gap-2 text-sm font-600 px-4 py-2 text-red-500 hover:bg-red-50 border border-red-100 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-logout-box-r-line" />
                  Sair de todos os outros dispositivos
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Security tip */}
      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
          <i className="ri-shield-check-line text-emerald-600 text-sm" />
        </div>
        <div>
          <p className="text-xs font-600 text-emerald-800 mb-0.5">Dica de segurança</p>
          <p className="text-xs text-emerald-700 leading-relaxed">
            Se você ver um dispositivo desconhecido, desconecte-o imediatamente e altere sua senha.
            Mantenha apenas os dispositivos que você reconhece ativos.
          </p>
        </div>
      </div>
    </div>
  );
}
